<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"

import Button from "@/components/shared/Button.vue"
import { getImageSizeInches } from "@/utils/image"
import { reportSentry } from "@/utils/sentry"

const { t } = useI18n()

const { purProduct, isForPurchase } = defineProps({
  purProduct: { type: Object, required: true },
  isForPurchase: { type: Boolean, default: true },
})
const fileDimensions = ref<number[]>([])

// EXIF 에 DPI 가 없으면 getImageSizeInches 가 [0, 0] 을 준다. 그걸 그대로 그리면
// 화면에 "0 inch" 가 떠서, 실제로 0 인치인 파일처럼 보인다 — 실측할 수 없었다는
// 사실이 거짓 치수로 바뀌는 조용한 실패다. 유효한 값일 때만 치수를 표시한다.
const hasDimensions = computed(() => fileDimensions.value.length === 2 && fileDimensions.value.every((v) => v > 0))

async function updateDimensions() {
  fileDimensions.value = [0, 0]
  try {
    const url = purProduct.printingImage?.download

    if (url) {
      fileDimensions.value = await getImageSizeInches(url)
    }
  } catch (e) {
    console.error(e)
    reportSentry(e)
  }
}

onMounted(async () => {
  if (isForPurchase) await updateDimensions()
})
</script>

<template>
  <div class="d-flex align-items-center">
    <div v-if="isForPurchase" class="col-auto d-flex flex-column">
      <div>{{ t("shared.fabric.warp") }}</div>
      <div class="mt-1">{{ hasDimensions ? `${fileDimensions[1]} inch` : "-" }}</div>
    </div>
    <div class="col ms-2 d-flex flex-column align-items-center">
      <div v-if="isForPurchase" class="d-flex mb-1">
        <div>{{ t("shared.fabric.weft") }}</div>
        <div class="ms-2">{{ hasDimensions ? `${fileDimensions[0]} inch` : "-" }}</div>
      </div>
      <img :src="purProduct.printingImage?.large" />
    </div>
  </div>
  <div v-if="isForPurchase" class="d-flex justify-content-center mt-3">
    <Button type="link" :text="t('shared.download')" :path="purProduct.printingImage?.download" :external="true" />
  </div>
</template>

<style scoped>
img {
  width: 100%;
}
</style>
